/**
 * Individual particle class for the advanced particle system
 * Manages the state and logic of a single particle with full ParticleProperties support
 */

import * as PIXI from 'pixi.js';
import type { ResolvedParticleData } from './ParticleProperties';
import { interpolateValue, interpolateColor } from './ParticleTypes';

/**
 * Manages the state and logic of a single particle.
 * This class works in pair with ParticleSystem and is not intended for standalone use.
 * @internal
 */
export class Particle {
  public readonly particle: PIXI.Particle; // In PIXI 8 we use Particle for ParticleContainer
  public isActive: boolean = false;

  // Particle properties
  private x: number = 0;
  private y: number = 0;
  private velocityX: number = 0;
  private velocityY: number = 0;
  private life: number = 0;
  private maxLife: number = 1;
  private rotation: number = 0;

  // New advanced properties
  private resolvedData?: ResolvedParticleData;
  private oscillationTime: number = 0;
  // Убираем неиспользуемые переменные - осцилляция теперь работает без накопления
  private trailTimer: number = 0;
  private lastTrailPosition: { x: number; y: number } = { x: 0, y: 0 };

  constructor() {
    // In Pixi v8 we use Particle for ParticleContainer
    this.particle = new PIXI.Particle({
      texture: PIXI.Texture.EMPTY,
      anchorX: 0.5,
      anchorY: 0.5,
    });
  }

  /**
   * Initialize particle with ParticleProperties system
   */
  public init(data: ResolvedParticleData, startX: number, startY: number): void {
    this.resolvedData = data;

    // Basic properties
    this.x = startX;
    this.y = startY;
    this.life = data.lifetime;
    this.maxLife = data.lifetime;

    // Velocity from resolved data
    this.velocityX = data.vx;
    this.velocityY = data.vy;

    // Advanced properties
    this.oscillationTime = 0;
    this.trailTimer = 0;
    this.lastTrailPosition = { x: startX, y: startY };

    // Rotation
    this.rotation = data.rotation.initial;

    // Setup particle display
    this.particle.texture = data.texture; // data.texture is already resolved to PIXI.Texture

    this.particle.x = this.x;
    this.particle.y = this.y;
    this.particle.tint = data.color.start;
    this.particle.alpha = data.alpha.start;
    this.particle.scaleX = data.scale.start;
    this.particle.scaleY = data.scale.start;
    this.particle.rotation = this.rotation;
    // Note: PIXI.Particle doesn't have blendMode property in v8
    // Blend mode is handled at container level

    this.isActive = true;
  }

  /**
   * Updates particle state.
   * @param dt - DeltaTime in seconds.
   * @returns `false` if particle "died", otherwise `true`.
   */
  public update(dt: number): boolean {
    if (!this.isActive) {
      return false;
    }

    this.life -= dt;
    if (this.life <= 0) {
      // Handle spawnOnDeath before deactivating
      if (this.resolvedData?.spawnOnDeath) {
        this._handleSpawnOnDeath();
      }
      this.isActive = false;
      return false;
    }

    return this.updateAdvanced(dt);
  }

  /**
   * Update method with full ParticleProperties support
   */
  private updateAdvanced(dt: number): boolean {
    if (!this.resolvedData) return false;

    const data = this.resolvedData;
    const lifeRatio = 1 - this.life / this.maxLife;

    // Physics with acceleration
    this.velocityX += data.acceleration.x * dt;
    this.velocityY += data.acceleration.y * dt;
    this.velocityX *= data.friction;
    this.velocityY *= data.friction;

    // Interpolate speed with easing
    const spd = interpolateValue(data.speed.start, data.speed.end, lifeRatio, data.speed.ease);
    const dirX =
      this.velocityX === 0 && this.velocityY === 0 ? 1 : Math.hypot(this.velocityX, this.velocityY);
    const nx = this.velocityX / (dirX || 1);
    const ny = this.velocityY / (dirX || 1);
    this.velocityX = nx * spd;
    this.velocityY = ny * spd;

    // Update position
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;

    // Осцилляция: считаем оффсет и применяем ТОЛЬКО к рендер-положению
    let renderX = this.x;
    let renderY = this.y;
    if (data.oscillation) {
      this.oscillationTime += dt;
      const off =
        Math.sin(this.oscillationTime * data.oscillation.frequency * Math.PI * 2) *
        data.oscillation.amplitude;
      const speed = Math.max(1e-6, Math.hypot(this.velocityX, this.velocityY));
      const nx = -this.velocityY / speed; // перпендикуляр
      const ny = this.velocityX / speed;
      // новый оффсет
      const oscX = nx * off;
      const oscY = ny * off;
      renderX += oscX;
      renderY += oscY;
    }

    // Rotation
    this.rotation += data.rotation.speed * dt;

    // Interpolate properties with easing
    const currentScale = interpolateValue(
      data.scale.start,
      data.scale.end,
      lifeRatio,
      data.scale.ease
    );
    const currentColor = interpolateColor(
      data.color.start,
      data.color.end,
      lifeRatio,
      data.color.ease
    );
    const currentAlpha = interpolateValue(
      data.alpha.start,
      data.alpha.end,
      lifeRatio,
      data.alpha.ease
    );

    // Update particle display
    this.particle.x = renderX;
    this.particle.y = renderY;
    this.particle.rotation = this.rotation;
    this.particle.scaleX = currentScale;
    this.particle.scaleY = currentScale;
    this.particle.tint = currentColor;
    this.particle.alpha = currentAlpha;

    // Trail effect
    if (data.trail) {
      this.trailTimer += dt;
      const distance = Math.sqrt(
        Math.pow(this.x - this.lastTrailPosition.x, 2) +
          Math.pow(this.y - this.lastTrailPosition.y, 2)
      );

      if (distance >= data.trail.spacing) {
        // Create trail particle (this would need to be handled by the system)
        this.lastTrailPosition = { x: this.x, y: this.y };
        this.trailTimer = 0;
      }
    }

    // Follow target effect
    if (data.followTarget) {
      const dx = data.followTarget.x - this.x;
      const dy = data.followTarget.y - this.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 0) {
        const targetAngle = Math.atan2(dy, dx);
        const currentAngle = Math.atan2(this.velocityY, this.velocityX);
        let angleDiff = targetAngle - currentAngle;

        // Normalize angle difference
        while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
        while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

        const maxTurn = data.followTarget.maxTurnRate * dt;
        const turnAmount = Math.max(-maxTurn, Math.min(maxTurn, angleDiff));
        const newAngle = currentAngle + turnAmount;

        const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
        this.velocityX = Math.cos(newAngle) * speed;
        this.velocityY = Math.sin(newAngle) * speed;
      }
    }

    // Orbit effect
    if (data.orbit) {
      const dx = this.x - data.orbit.centerX;
      const dy = this.y - data.orbit.centerY;
      const currentRadius = Math.sqrt(dx * dx + dy * dy);

      if (data.orbit.maintainRadius && currentRadius !== data.orbit.radius) {
        const angle = Math.atan2(dy, dx);
        this.x = data.orbit.centerX + Math.cos(angle) * data.orbit.radius;
        this.y = data.orbit.centerY + Math.sin(angle) * data.orbit.radius;
      }

      // Apply orbital velocity
      const orbitalAngle = Math.atan2(dy, dx) + data.orbit.speed * dt;
      const orbitalX = Math.cos(orbitalAngle) * data.orbit.radius;
      const orbitalY = Math.sin(orbitalAngle) * data.orbit.radius;

      this.velocityX += (orbitalX - dx) * dt;
      this.velocityY += (orbitalY - dy) * dt;
    }

    return true;
  }

  /**
   * Handle spawnOnDeath effects
   */
  private _handleSpawnOnDeath(): void {
    if (!this.resolvedData?.spawnOnDeath) return;

    // This would need access to ParticleSystem to emit new particles
    // For now, we'll just log it - in a real implementation, we'd need
    // to pass a reference to ParticleSystem or use an event system
    console.log('Particle death spawn effects:', Object.keys(this.resolvedData.spawnOnDeath));
  }

  /**
   * Reset particle to inactive state
   */
  public reset(): void {
    this.isActive = false;
    this.resolvedData = undefined;
    this.oscillationTime = 0;
    this.trailTimer = 0;
    this.lastTrailPosition = { x: 0, y: 0 };
  }
}
